<template>
  <div class="feed-setup-modal">
    <TextInput
      id="feed_name"
      label="Feed Naam"
      v-model="feedForm.name"
      placeholder="Voer feed naam in"
      :rules="['required', 'maxLength:255']"
      @validate="handleValidation('name', $event)"
    />

    <TextInput
      id="feed_description"
      label="Beschrijving"
      v-model="feedForm.description"
      placeholder="Optionele beschrijving"
      :rules="['maxLength:500']"
      @validate="handleValidation('description', $event)"
    />

    <SelectInput
      id="feed_purpose"
      label="Gebruik"
      v-model="feedForm.purpose"
      :options="purposeOptions"
      info="Materiaal is voor losse artikelen op werkbonnen en inkooporders. Die producten verschijnen niet in configurators, offertes en verkooporders."
    />

    <!-- Attributen sectie -->
    <div class="settings-section" style="display: none">
      <h3>Feed Attributen</h3>
      <p class="section-description">
        Definieer attributen die producten in deze feed kunnen hebben (bijv. kleur, maat, type)
      </p>

      <div v-if="attributes.length === 0" class="empty-state">
        <p>Nog geen attributen toegevoegd</p>
      </div>

      <div v-else class="attributes-list">
        <div v-for="(attr, index) in attributes" :key="index" class="attribute-item">
          <div class="attribute-info">
            <strong>{{ attr.label }} ({{ attr.key }})</strong>
            <span class="attribute-type">{{ attr.type }}</span>
            <span v-if="attr.type === 'select' && attr.options?.length" class="attribute-options">
              [{{ attr.options.map((o) => o.label || o.value).join(', ') }}]
            </span>
          </div>
          <div class="attribute-actions">
            <Btn variant="secondary" size="sm" @click="editAttribute(index)">Bewerken</Btn>
            <Btn variant="danger" size="sm" @click="removeAttribute(index)">Verwijderen</Btn>
          </div>
        </div>
      </div>

      <Btn variant="outline" @click="showAttributeModal = true" class="mt-3">
        <BaseIcon name="plus" size="16" />
        Attribuut Toevoegen
      </Btn>
    </div>

    <!-- Attribute Modal -->
    <div v-if="showAttributeModal" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>
            {{ editingAttributeIndex !== null ? 'Attribuut Bewerken' : 'Attribuut Toevoegen' }}
          </h3>
          <button @click="closeAttributeModal" class="close-btn">×</button>
        </div>

        <div class="modal-body">
          <TextInput
            id="attr-key"
            label="Attribuut Key"
            v-model="attributeForm.key"
            placeholder="bijv. color, size, type"
            :disabled="editingAttributeIndex !== null"
            :rules="['required']"
          />

          <TextInput
            id="attr-label"
            label="Attribuut Label"
            v-model="attributeForm.label"
            placeholder="bijv. Kleur, Maat, Type"
            :rules="['required']"
          />

          <SelectInput
            id="attr-type"
            label="Type"
            v-model="attributeForm.type"
            :options="[
              { label: 'Tekst', value: 'text' },
              { label: 'Nummer', value: 'number' },
              { label: 'Selectie', value: 'select' },
            ]"
          />

          <div v-if="attributeForm.type === 'select'" class="options-section">
            <label>Opties</label>
            <div v-for="(option, idx) in attributeForm.options" :key="idx" class="option-item">
              <TextInput
                :id="`option-value-${idx}`"
                label="Waarde"
                v-model="option.value"
                placeholder="bijv. red, blue"
                :rules="['required']"
              />
              <TextInput
                :id="`option-label-${idx}`"
                label="Label"
                v-model="option.label"
                placeholder="bijv. Rood, Blauw"
                :rules="['required']"
              />
              <Btn variant="danger" size="sm" @click="removeOption(idx)">Verwijderen</Btn>
            </div>
            <Btn variant="outline" size="sm" @click="addOption">Optie Toevoegen</Btn>
          </div>
        </div>

        <div class="modal-footer">
          <Btn variant="secondary" @click="closeAttributeModal">Annuleren</Btn>
          <Btn variant="primary" @click="saveAttribute">
            {{ editingAttributeIndex !== null ? 'Bijwerken' : 'Toevoegen' }}
          </Btn>
        </div>
      </div>
    </div>

    <!-- Modal Actions -->
    <div class="modal__actions">
      <Btn
        v-if="isEdit"
        text="Feed Verwijderen"
        @click="deleteFeed"
        variant="danger"
        class="mr-2"
      />
      <Btn
        :text="isEdit ? 'Wijzigingen Opslaan' : 'Feed Aanmaken'"
        @click="handleSubmit"
        variant="primary"
        :loading="loading"
        :disabled="loading"
      />
    </div>
  </div>
</template>

<script>
  import { ref, reactive, computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@/components/partials/BaseIcon.vue';

  export default {
    name: 'FeedSetupModal',

    components: {
      TextInput,
      SelectInput,
      Btn,
      BaseIcon,
    },

    props: {
      feed: {
        type: Object,
        default: () => ({
          name: '',
          description: '',
          attributes: [],
        }),
      },
      isEdit: {
        type: Boolean,
        default: false,
      },
    },

    setup(props, { emit }) {
      const { t } = useI18n();
      const loading = ref(false);
      const showAttributeModal = ref(false);
      const editingAttributeIndex = ref(null);
      const attributes = ref([...(props.feed.attributes || [])]);
      const validationErrors = ref({});

      const feedForm = reactive({
        name: props.feed.name || '',
        description: props.feed.description || '',
        purpose: props.feed.purpose || 'sales',
      });

      const purposeOptions = [
        { value: 'sales', label: 'Verkoop (configurator, offertes en orders)' },
        { value: 'materials', label: 'Materiaal (werkbonnen en inkoop)' },
      ];

      const attributeForm = reactive({
        key: '',
        label: '',
        type: 'text',
        options: [],
      });

      // Handle validation from your input components
      const handleValidation = (fieldName, errorMessage) => {
        if (errorMessage) {
          validationErrors.value[fieldName] = errorMessage;
        } else {
          delete validationErrors.value[fieldName];
        }
      };

      // Check if form is valid
      const isFormValid = computed(() => {
        return Object.keys(validationErrors.value).length === 0 && feedForm.name.trim() !== '';
      });

      // Attribute management
      const resetAttributeForm = () => {
        attributeForm.key = '';
        attributeForm.label = '';
        attributeForm.type = 'text';
        attributeForm.options = [];
      };

      const addOption = () => {
        attributeForm.options.push({ value: '', label: '' });
      };

      const removeOption = (index) => {
        attributeForm.options.splice(index, 1);
      };

      const editAttribute = (index) => {
        const attr = attributes.value[index];
        attributeForm.key = attr.key;
        attributeForm.label = attr.label;
        attributeForm.type = attr.type;
        attributeForm.options = [...(attr.options || [])];
        editingAttributeIndex.value = index;
        showAttributeModal.value = true;
      };

      const removeAttribute = (index) => {
        if (confirm('Weet je zeker dat je dit attribuut wilt verwijderen?')) {
          attributes.value.splice(index, 1);
        }
      };

      const saveAttribute = () => {
        // Validate attribute form
        if (!attributeForm.key || !attributeForm.label) {
          alert('Key en Label zijn verplicht');
          return;
        }

        // Check for duplicate keys (except when editing)
        const existingIndex = attributes.value.findIndex((attr) => attr.key === attributeForm.key);
        if (existingIndex !== -1 && existingIndex !== editingAttributeIndex.value) {
          alert('Een attribuut met deze key bestaat al');
          return;
        }

        const newAttribute = {
          key: attributeForm.key,
          label: attributeForm.label,
          type: attributeForm.type,
          ...(attributeForm.type === 'select' && { options: [...attributeForm.options] }),
        };

        if (editingAttributeIndex.value !== null) {
          attributes.value[editingAttributeIndex.value] = newAttribute;
        } else {
          attributes.value.push(newAttribute);
        }

        closeAttributeModal();
      };

      const closeAttributeModal = () => {
        showAttributeModal.value = false;
        editingAttributeIndex.value = null;
        resetAttributeForm();
      };

      // Main form submission
      const handleSubmit = async () => {
        if (!isFormValid.value) {
          alert('Vul alle verplichte velden in');
          return;
        }

        loading.value = true;

        try {
          const feedData = {
            name: feedForm.name,
            description: feedForm.description,
            purpose: feedForm.purpose,
            attributes: attributes.value,
          };

          await emit('submit', feedData);
        } catch (error) {
          console.error('Failed to submit feed:', error);
        } finally {
          loading.value = false;
        }
      };

      const deleteFeed = () => {
        emit('delete-feed', props.feed.id);
      };

      return {
        feedForm,
        purposeOptions,
        loading,
        attributes,
        showAttributeModal,
        editingAttributeIndex,
        attributeForm,
        isEdit: props.isEdit,
        handleValidation,
        handleSubmit,
        deleteFeed,
        editAttribute,
        removeAttribute,
        saveAttribute,
        closeAttributeModal,
        addOption,
        removeOption,
      };
    },
  };
</script>

<style scoped lang="scss">
  .feed-setup-modal {
    .settings-section {
      margin: 1.5rem 0;

      h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--slate-900);
      }

      .section-description {
        margin: 0 0 1rem 0;
        font-size: 0.875rem;
        color: var(--slate-600);
      }
    }

    .empty-state {
      padding: 2rem;
      text-align: center;
      color: var(--slate-500);
      background: var(--slate-50);
      border-radius: 6px;
      border: 1px dashed var(--slate-300);
    }

    .attributes-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .attribute-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem;
      background: var(--slate-50);
      border-radius: 6px;
      border: 1px solid var(--slate-200);

      .attribute-info {
        flex: 1;

        strong {
          color: var(--slate-900);
        }

        .attribute-type {
          margin-left: 0.5rem;
          padding: 0.125rem 0.5rem;
          background: var(--blue-100);
          color: var(--blue-700);
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .attribute-options {
          margin-left: 0.5rem;
          font-size: 0.875rem;
          color: var(--slate-600);
        }
      }

      .attribute-actions {
        display: flex;
        gap: 0.5rem;
      }
    }

    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .modal-content {
      background: white;
      border-radius: 8px;
      width: 90%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem;
      border-bottom: 1px solid var(--slate-200);

      h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
      }

      .close-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: var(--slate-500);

        &:hover {
          color: var(--slate-700);
        }
      }
    }

    .modal-body {
      padding: 1.5rem;

      .options-section {
        margin-top: 1rem;

        label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }
      }

      .option-item {
        display: flex;
        gap: 0.5rem;
        align-items: end;
        margin-bottom: 0.75rem;

        > div {
          flex: 1;
        }
      }
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      padding: 1.5rem;
      border-top: 1px solid var(--slate-200);
    }

    .modal__actions {
      display: flex;
      justify-content: space-between;
      margin-top: 2rem;
      padding-top: 1.5rem;
      border-top: 1px solid var(--slate-200);

      .btn:only-child {
        margin-left: auto;
      }
    }

    .mt-3 {
      margin-top: 0.75rem;
    }
  }
</style>
